import { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'

import { API } from '@/api'
import { Header } from '@/components/Header.tsx'
import { LoginScope } from '@/consts/team'
import { AppContext, Application } from '@/contexts/AppContext'
import { InstanceContextProvider } from '@/contexts/InstanceContext'
import { UnifiedContextProvider } from '@/contexts/UnifiedContext'
import { EduVerify } from '@/models/profile.ts'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal.tsx'
import { useTeamStore } from '@/stores/team'
import { useIsLoggedIn, useUserStore } from '@/stores/user'

import { Menu } from './Menu'

export function Layout() {
  const userStore = useUserStore()
  const isLoggedIn = useIsLoggedIn()
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)
  const [eduVerify, setEduVerify] = useState<EduVerify | undefined>(undefined)

  const [refreshKey, setRefreshKey] = useState('')

  useEffect(() => {
    if (isLoggedIn) {
      userStore.getOrder()
    }
  }, [refreshKey, isLoggedIn])

  useEffect(() => {
    if (isLoggedIn) {
      API.getEduVerifyStatus().then((res) => {
        setEduVerify(res.eduVerify)
      })
    }
  }, [isLoggedIn])

  // Sync context when entering platform routes via browser back/forward
  useEffect(() => {
    const teamStore = useTeamStore.getState()

    if (teamStore.scopeInfo?.scope === LoginScope.Team) {
      // Browser back/forward navigated from team to platform
      // Clear team context and re-fetch platform user info
      teamStore.clearTeamContext()
      teamStore.setScopeInfo(null)
      userStore.getUserInfo()
    } else if (teamStore.teamTokenData) {
      // scopeInfo is already correct (platform), but stale team token remains
      // Just clean it up without re-fetching
      teamStore.clearTeamContext()
    }
  }, [])

  if (!isLoggedIn) {
    return <Navigate to="login" replace />
  }

  const app: Application = {
    refresh: () => setRefreshKey(Math.random().toString(16)),
  }

  return (
    <AppContext.Provider value={app}>
      <UnifiedContextProvider mode="platform">
        <InstanceContextProvider mode="platform">
          <div className="h-full flex flex-col overflow-hidden bg-[image:var(--image-app-background)] bg-cover bg-no-repeat bg-center">
            <Header setExchargeModalOpen={setExchargeModalOpen} eduVerify={eduVerify} />
            <main className="w-full flex-1 bg-cover bg-no-repeat bg-center pb-4 flex items-start overflow-auto">
              <div className="min-w-[1400px] w-full flex gap-4 px-4 h-full">
                <Menu />
                <div className="flex-1 overflow-y-auto rounded-[32px] relative">
                  <Outlet />
                </div>
              </div>
            </main>
          </div>
          {exchargeModalOpen && (
            <ExchargeModal
              onOk={() => {
                setExchargeModalOpen(false)
                userStore.getUserInfo()
              }}
              onClose={() => {
                setExchargeModalOpen(false)
              }}
            />
          )}
        </InstanceContextProvider>
      </UnifiedContextProvider>
    </AppContext.Provider>
  )
}
